import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ClientConfigResponse {
  code: string;
  isActive: boolean;
  client: {
    name: string;
    subtitle: string;
    logo: string;
    favicon: string;
  };
  branding: {
    primaryColor: string;
    primaryHover: string;
    primaryDark: string;
    primaryLight: string;
    accentColor: string;
    accentLight: string;
    backgroundColor: string;
    cardBackground: string;
    textPrimary: string;
    textMuted: string;
    borderColor: string;
  };
  kiosk: {
    exitPassword: string;
    inactivityTimeoutSeconds: number;
    showBestScores: boolean;
  };
  games: {
    memory: { enabled: boolean; pairs: number };
    puzzle: { enabled: boolean };
    reaction: { enabled: boolean };
    simon: { enabled: boolean; colors: number; speedMs: number };
    whack: { enabled: boolean; durationSeconds: number };
    speedtap: { enabled: boolean; durationSeconds: number };
    balloonpop: { enabled: boolean; durationSeconds: number };
  };
  records: {
    enabled: boolean;
    requireRut: boolean;
    requireName: boolean;
    maxEntriesPerGame: number;
  };
  updatedAt: string;
}

export interface ClientListItem {
  id: number;
  code: string;
  is_active: boolean;
  client_name: string;
  client_subtitle: string;
  branding_primary_color: string;
  leaderboard_count: number;
  updated_at: string;
}

export interface LeaderboardEntry {
  id: number;
  client_code: string;
  game_id: string;
  player_name: string;
  player_rut: string;
  score: number;
  is_higher_better: boolean;
  created_at: string;
}

@Injectable({
  providedIn: 'root',
})
export class AdminApiService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // ── Clients ──

  getClients(): Observable<ClientListItem[]> {
    return this.http.get<ClientListItem[]>(`${this.baseUrl}/clients/`);
  }

  getClient(code: string): Observable<ClientConfigResponse> {
    return this.http.get<ClientConfigResponse>(`${this.baseUrl}/clients/${code}/`);
  }

  createClient(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/clients/`, data);
  }

  updateClient(code: string, data: any): Observable<any> {
    return this.http.patch(`${this.baseUrl}/clients/${code}/`, data);
  }

  deleteClient(code: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/clients/${code}/`);
  }

  uploadLogo(code: string, file: File): Observable<{ logo: string }> {
    const form = new FormData();
    form.append('logo', file);
    return this.http.post<{ logo: string }>(`${this.baseUrl}/clients/${code}/upload-logo/`, form);
  }

  uploadFavicon(code: string, file: File): Observable<{ favicon: string }> {
    const form = new FormData();
    form.append('favicon', file);
    return this.http.post<{ favicon: string }>(`${this.baseUrl}/clients/${code}/upload-favicon/`, form);
  }

  // ── Leaderboard ──

  getLeaderboard(params?: { client?: string; game_id?: string; search?: string }): Observable<any> {
    let query = '';
    if (params) {
      const parts: string[] = [];
      if (params.client) parts.push(`client=${params.client}`);
      if (params.game_id) parts.push(`game_id=${params.game_id}`);
      if (params.search) parts.push(`search=${params.search}`);
      if (parts.length) query = '?' + parts.join('&');
    }
    return this.http.get<any>(`${this.baseUrl}/leaderboard/${query}`);
  }

  getTopScores(clientCode: string, gameId: string, limit = 10): Observable<LeaderboardEntry[]> {
    return this.http.get<LeaderboardEntry[]>(
      `${this.baseUrl}/leaderboard/top/${clientCode}/${gameId}/?limit=${limit}`
    );
  }

  deleteLeaderboardEntry(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/leaderboard/${id}/`);
  }

  clearLeaderboard(params?: { client?: string; game_id?: string }): Observable<{ deleted: number }> {
    let query = '';
    if (params) {
      const parts: string[] = [];
      if (params.client) parts.push(`client=${params.client}`);
      if (params.game_id) parts.push(`game_id=${params.game_id}`);
      if (parts.length) query = '?' + parts.join('&');
    }
    return this.http.delete<{ deleted: number }>(`${this.baseUrl}/leaderboard/clear/${query}`);
  }
}
